// src/pages/admin/AdminGiftCards.jsx
import { useEffect, useState, useCallback } from "react";
import adminApi from "../../api/adminApi";
import { toast } from "react-toastify";
import { RefreshCw, Save, Search, Wallet, CheckCircle2, AlertTriangle } from "lucide-react";

const naira = (n) => `₦${Number(n || 0).toLocaleString("en-NG")}`;
const field =
  "px-3 py-2 text-sm bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 dark:text-white";
const errMsg = (e, d) => e.response?.data?.message || d;

const ST = {
  completed:    "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300",
  processing:   "bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-300",
  submitting:   "bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-300",
  needs_review: "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300",
  failed:       "bg-gray-200 text-gray-700 dark:bg-gray-700 dark:text-gray-200",
  refunded:     "bg-gray-200 text-gray-700 dark:bg-gray-700 dark:text-gray-200",
};

export default function AdminGiftCards() {
  const [loading, setLoading] = useState(true);
  const [settings, setSettings] = useState(null);
  const [brands, setBrands] = useState([]);
  const [ready, setReady] = useState({});
  const [balance, setBalance] = useState(undefined);
  const [edits, setEdits] = useState({});
  const [search, setSearch] = useState("");
  const [saving, setSaving] = useState("");
  const [orders, setOrders] = useState([]);
  const [counts, setCounts] = useState({});
  const [status, setStatus] = useState("");
  const [form, setForm] = useState({ defaultMargin: "1.2", notice: "" });

  const loadSettings = useCallback(async () => {
    try {
      const r = await adminApi.get("/giftcards/settings");
      setSettings(r.data.settings);
      setBrands(r.data.brands);
      setReady(r.data.ready);
      setForm({ defaultMargin: String(r.data.settings.defaultMargin), notice: r.data.settings.notice || "" });
      setEdits({});
    } catch (e) { toast.error(errMsg(e, "Failed to load gift card settings")); }
  }, []);

  const loadOrders = useCallback(async () => {
    try {
      const r = await adminApi.get("/giftcards/orders", { params: status ? { status } : {} });
      setOrders(r.data.orders);
      setCounts(r.data.counts || {});
    } catch (e) { toast.error(errMsg(e, "Failed to load orders")); }
  }, [status]);

  const loadBalance = async () => {
    setBalance(null);
    try {
      const r = await adminApi.get("/giftcards/balance");
      setBalance(r.data.availableNgn);
    } catch { setBalance(undefined); toast.error("Could not read supplier balance"); }
  };

  useEffect(() => { (async () => { await loadSettings(); setLoading(false); })(); }, [loadSettings]);
  useEffect(() => { loadOrders(); }, [loadOrders]);

  const saveGeneral = async (patch) => {
    setSaving("general");
    try {
      const r = await adminApi.put("/giftcards/settings", patch);
      setSettings(r.data.settings);
      toast.success("Saved");
      loadSettings();
    } catch (e) { toast.error(errMsg(e, "Save failed")); }
    finally { setSaving(""); }
  };

  const saveBrandMargin = async (b) => {
    const raw = edits[b.brandId];
    setSaving(b.brandId);
    try {
      await adminApi.put("/giftcards/brand-margin", { brandId: b.brandId, margin: raw === "" || raw === undefined ? null : Number(raw) });
      toast.success(`${b.name} margin saved`);
      loadSettings();
    } catch (e) { toast.error(errMsg(e, "Save failed")); }
    finally { setSaving(""); }
  };

  const toggleHidden = async (b) => {
    try {
      await adminApi.put("/giftcards/brand-visibility", { brandId: b.brandId, hidden: !b.hidden });
      loadSettings();
    } catch (e) { toast.error(errMsg(e, "Failed")); }
  };

  const act = async (o, kind) => {
    if (kind === "refund" && !window.confirm(`Refund ${naira(o.priceNgn)} to ${o.user?.username || "this user"}? Only do this if the card was NOT delivered.`)) return;
    try {
      const r = await adminApi.post(`/giftcards/orders/${o.id}/${kind}`);
      toast.success(r.data.message || "Done");
      loadOrders();
    } catch (e) { toast.error(errMsg(e, "Action failed")); }
  };

  if (loading) return <div className="flex justify-center py-20"><RefreshCw className="animate-spin text-purple-500" /></div>;

  const filtered = brands.filter((b) => b.name.toLowerCase().includes(search.toLowerCase()));
  const dm = Number(form.defaultMargin);
  const dmValid = dm >= 1 && dm <= 3;

  return (
    <div className="space-y-6 max-w-5xl">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold text-gray-900 dark:text-white">Gift Cards</h1>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">Margins, visibility and orders for gift cards</p>
        </div>
        <button onClick={() => { loadSettings(); loadOrders(); }} className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-gray-600 dark:text-gray-300 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg">
          <RefreshCw size={14} /> Refresh
        </button>
      </div>

      {/* Status strip */}
      <div className="grid sm:grid-cols-3 gap-3">
        {[["Supplier keys", ready.sendpadi], ["Card encryption key", ready.encryption]].map(([label, ok]) => (
          <div key={label} className="flex items-center gap-2 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-xl px-4 py-3">
            {ok ? <CheckCircle2 size={16} className="text-green-500" /> : <AlertTriangle size={16} className="text-red-500" />}
            <span className="text-sm text-gray-800 dark:text-gray-200">{label}: {ok ? "OK" : "Missing"}</span>
          </div>
        ))}
        <div className="flex items-center justify-between gap-2 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-xl px-4 py-3">
          <span className="flex items-center gap-2 text-sm text-gray-800 dark:text-gray-200">
            <Wallet size={16} className="text-purple-500" />
            {balance === undefined ? "Supplier balance" : balance === null ? "Loading…" : naira(balance)}
          </span>
          <button onClick={loadBalance} className="text-xs text-purple-600 dark:text-purple-400 font-medium">Check</button>
        </div>
      </div>

      {/* General settings */}
      <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 p-5 space-y-4">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-sm font-semibold text-gray-900 dark:text-white">Selling</h2>
            <p className="text-xs text-gray-500 dark:text-gray-400">Turn off to stop all new gift card purchases immediately.</p>
          </div>
          <button
            onClick={() => saveGeneral({ enabled: !settings.enabled })}
            className={`px-4 py-2 rounded-lg text-sm font-semibold text-white ${settings.enabled ? "bg-green-600 hover:bg-green-700" : "bg-gray-500 hover:bg-gray-600"}`}
          >
            {settings.enabled ? "ON — click to pause" : "OFF — click to enable"}
          </button>
        </div>

        <div>
          <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1.5">
            Default margin (price = SendPadi cost × margin). Current markup: {dmValid ? Math.round((dm - 1) * 100) : "—"}%
          </label>
          <div className="flex flex-wrap items-center gap-2">
            <input type="number" min="1" max="3" step="0.01" value={form.defaultMargin} onChange={(e) => setForm({ ...form, defaultMargin: e.target.value })} className={`${field} w-28 font-mono`} />
            {["1.05", "1.10", "1.15", "1.20", "1.30"].map((p) => (
              <button key={p} onClick={() => setForm({ ...form, defaultMargin: p })}
                className={`px-2.5 py-1 text-xs rounded-lg border font-mono ${form.defaultMargin === p ? "bg-purple-600 text-white border-purple-600" : "bg-gray-50 dark:bg-gray-800 text-gray-600 dark:text-gray-300 border-gray-200 dark:border-gray-700"}`}>
                {p}x
              </button>
            ))}
          </div>
          {!dmValid && <p className="text-xs text-red-500 mt-1">Must be between 1.0 and 3.0</p>}
        </div>

        <div>
          <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1.5">Notice shown to users (optional)</label>
          <input maxLength={200} value={form.notice} onChange={(e) => setForm({ ...form, notice: e.target.value })} placeholder="e.g. Delivery may take a few minutes today" className={`${field} w-full`} />
        </div>

        <button onClick={() => saveGeneral({ defaultMargin: dm, notice: form.notice })} disabled={!dmValid || saving === "general"}
          className="flex items-center gap-2 px-5 py-2.5 bg-purple-600 hover:bg-purple-700 disabled:bg-purple-400 text-white text-sm font-medium rounded-lg">
          <Save size={14} /> Save settings
        </button>
      </div>

      {/* Per-brand */}
      <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800">
        <div className="flex items-center gap-3 px-5 py-4 border-b border-gray-100 dark:border-gray-800">
          <div className="flex-1">
            <h2 className="text-sm font-semibold text-gray-900 dark:text-white">Brands</h2>
            <p className="text-xs text-gray-500 dark:text-gray-400">Leave the margin empty to use the default. Hidden brands disappear from the shop.</p>
          </div>
          <div className="relative">
            <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" />
            <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search" className={`${field} pl-8 w-40`} />
          </div>
        </div>
        <div className="max-h-[480px] overflow-y-auto divide-y divide-gray-100 dark:divide-gray-800">
          {filtered.length === 0 && <p className="p-5 text-sm text-gray-500">No brands loaded. Check the supplier keys above.</p>}
          {filtered.map((b) => {
            const val = edits[b.brandId] ?? (b.overrideMargin ?? "");
            return (
              <div key={b.brandId} className={`flex flex-wrap items-center gap-3 px-5 py-3 ${b.hidden ? "opacity-60" : ""}`}>
                <div className="flex-1 min-w-[140px]">
                  <p className="text-sm font-medium text-gray-900 dark:text-white">{b.name}</p>
                  <p className="text-xs text-gray-400">{b.productCount} products · using {b.effectiveMargin}x</p>
                </div>
                <input type="number" min="1" max="3" step="0.01" value={val} placeholder={String(settings.defaultMargin)}
                  onChange={(e) => setEdits({ ...edits, [b.brandId]: e.target.value })} className={`${field} w-24 font-mono`} />
                <button onClick={() => saveBrandMargin(b)} disabled={saving === b.brandId || edits[b.brandId] === undefined}
                  className="px-3 py-2 text-xs font-medium rounded-lg bg-purple-600 hover:bg-purple-700 disabled:bg-gray-300 dark:disabled:bg-gray-700 text-white">
                  Save
                </button>
                <button onClick={() => toggleHidden(b)}
                  className={`px-3 py-2 text-xs font-medium rounded-lg border ${b.hidden ? "border-green-300 text-green-700" : "border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-300"}`}>
                  {b.hidden ? "Show" : "Hide"}
                </button>
              </div>
            );
          })}
        </div>
      </div>

      {/* Orders */}
      <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800">
        <div className="flex flex-wrap items-center gap-3 px-5 py-4 border-b border-gray-100 dark:border-gray-800">
          <h2 className="text-sm font-semibold text-gray-900 dark:text-white flex-1">Orders</h2>
          {counts.needs_review > 0 && <span className="text-xs font-semibold text-red-600">{counts.needs_review} need review</span>}
          <select value={status} onChange={(e) => setStatus(e.target.value)} className={field}>
            <option value="">All</option>
            {["submitting", "processing", "completed", "needs_review", "failed", "refunded"].map((s) => (
              <option key={s} value={s}>{s.replace("_", " ")} {counts[s] ? `(${counts[s]})` : ""}</option>
            ))}
          </select>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full min-w-[780px] text-sm">
            <thead className="bg-gray-50 dark:bg-gray-800 text-xs text-gray-500 dark:text-gray-400">
              <tr>{["Date", "User", "Card", "Cost → Price (profit)", "Status", ""].map((h) => <th key={h} className="px-4 py-2.5 text-left font-medium">{h}</th>)}</tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
              {orders.length === 0 && <tr><td colSpan="6" className="px-4 py-10 text-center text-gray-500">No orders</td></tr>}
              {orders.map((o) => (
                <tr key={o.id} className="align-top">
                  <td className="px-4 py-3 text-xs text-gray-500 whitespace-nowrap">{new Date(o.createdAt).toLocaleString("en-NG")}</td>
                  <td className="px-4 py-3 text-gray-800 dark:text-gray-200">{o.user?.username || "—"}<p className="text-xs text-gray-400">{o.user?.email}</p></td>
                  <td className="px-4 py-3 text-gray-800 dark:text-gray-200">{o.brandName}<p className="text-xs text-gray-400">{o.faceValue} {o.currency} · {o.countryName}</p></td>
                  <td className="px-4 py-3 whitespace-nowrap text-gray-800 dark:text-gray-200">
                    {naira(o.providerChargedNgn ?? o.costNgn)} → {naira(o.priceNgn)}
                    <p className={`text-xs ${o.profitNgn < 0 ? "text-red-500" : "text-green-600"}`}>
                      {o.profitNgn >= 0 ? "+" : ""}{naira(o.profitNgn)} · {o.margin}x{o.costSource === "catalog" ? " · est. cost" : ""}
                    </p>
                    {o.costMismatch && <p className="text-xs text-red-500">Supplier charged more than quoted</p>}
                  </td>
                  <td className="px-4 py-3">
                    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${ST[o.status] || ""}`}>{o.status.replace("_", " ")}</span>
                    {o.lastError && <p className="text-xs text-red-500 mt-1 max-w-[220px] break-words">{o.lastError}</p>}
                    {o.status === "completed" && o.emailError && <p className="text-xs text-amber-600 mt-1">Email failed</p>}
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap space-x-2">
                    {o.status === "needs_review" && <button onClick={() => act(o, "retry")} className="px-2.5 py-1 text-xs rounded-lg bg-purple-600 text-white">Retry</button>}
                    {["submitting", "processing", "needs_review"].includes(o.status) && (
                      <button onClick={() => act(o, "refund")} className="px-2.5 py-1 text-xs rounded-lg border border-red-300 text-red-600">Refund</button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}